import type { ImpactType } from '~/scopes/campaign/data/metals/types'

export const CAMPAIGN_SLUG = 'mine-my-phone'

export const INTRO_STORAGE_KEY = 'campaign_intro_seen'

// Three responsive tiers, split at two breakpoints:
//   • phone   <768   — bottom sheets (drag, collapsible pill)
//   • tablet  768–1199 — one column at a time: each panel keeps the desktop edge it
//                        owns (impacts left, modals right) and the other side is map
//   • desktop ≥1200   — both at once: left impacts (580) + right modal (460), which
//                        first fit side by side at 1200 (128px of map between them)
// Both seams are DS screens — 768 is `md`, 1200 is `lg` — so the CSS mirrors them with
// `md:` / `lg:` / `max-lg:`; JS reads PHONE_MEDIA / DESKTOP_MEDIA.
export const TABLET_MIN_WIDTH = 768
export const DESKTOP_MIN_WIDTH = 1200
export const PHONE_MEDIA = '(max-width: 767.98px)'
export const DESKTOP_MEDIA = '(min-width: 1200px)'

// Mirrors of `--raw-regular` / `--raw-slow` (assets/css/easings.css), for the overlays
// whose fade lives on the root's *children* rather than the root — see the style block in
// `UI/ExtendedModal.vue`. Vue reads a Transition's timing off its root element, so those
// need `:duration` handed to them or they unmount before the children finish and the exit
// plays instantly. Keep in step with the CSS tokens; JS can't read them.
export const MOTION_REGULAR_MS = 400
export const MOTION_SLOW_MS = 600

export const MODAL_ROUTES = {
  ENVIRONMENTAL_IMPACT: 'environmental',
  HUMAN_IMPACT: 'human',
  GEOPOLITICAL_IMPACT: 'geopolitical',
  METAL_LIBRARY: 'metal',
  VIDEO_GALLERY: 'video',
  TAKE_ACTION: 'take-action',
} as const

export type ModalRoute = (typeof MODAL_ROUTES)[keyof typeof MODAL_ROUTES]

export const IMPACT_ROUTES: ModalRoute[] = [
  MODAL_ROUTES.ENVIRONMENTAL_IMPACT,
  MODAL_ROUTES.HUMAN_IMPACT,
  MODAL_ROUTES.GEOPOLITICAL_IMPACT,
]

// True when `modal` is one of the three impact routes. A type-guard so callers
// also narrow `ModalRoute | null` → `ModalRoute`.
export function isImpactRoute(modal: ModalRoute | null): modal is ModalRoute {
  return modal !== null && IMPACT_ROUTES.includes(modal)
}

// Maps an article's impact to its modal route. The string values are identical,
// so this is a typed lookup rather than a cast.
export const impactRoute = (impact: ImpactType): ModalRoute =>
  ({
    environmental: MODAL_ROUTES.ENVIRONMENTAL_IMPACT,
    human: MODAL_ROUTES.HUMAN_IMPACT,
    geopolitical: MODAL_ROUTES.GEOPOLITICAL_IMPACT,
  })[impact]
